import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Globe,
  MapPin,
  Search,
  Sparkles,
  UtensilsCrossed,
  Zap,
} from 'lucide-react';
import { buttonClasses } from '@/components/ui/button';
import { ExampleScoreCard } from './example-score-card';
import { Reveal } from '@/components/ui/reveal';
import type { CSSProperties } from 'react';

const d = (ms: number) => ({ '--delay': `${ms}ms` }) as CSSProperties;

export function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 -top-40 -z-10 h-[520px] bg-[radial-gradient(ellipse_at_top,var(--color-brand-100),transparent_65%)]" />
      <div aria-hidden className="pointer-events-none absolute -z-10 inset-0 overflow-hidden">
        <div className="animate-blob absolute -left-24 top-10 size-72 rounded-full bg-brand-200/50 blur-3xl" />
        <div className="animate-blob absolute right-0 top-32 size-80 rounded-full bg-amber-200/40 blur-3xl [animation-delay:-6s]" />
        <div className="animate-blob absolute bottom-0 left-1/3 size-64 rounded-full bg-emerald-200/30 blur-3xl [animation-delay:-12s]" />
      </div>
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:pt-24">
        <div>
          <p className="animate-fade-up inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-sm font-medium text-brand-800 shadow-sm ring-1 ring-brand-200">
            <Sparkles className="animate-sparkle size-4" aria-hidden /> Free · No sign-up needed
          </p>
          <h1
            className="animate-fade-up delay-var mt-5 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl"
            style={d(100)}
          >
            Is your website{' '}
            <span className="animate-gradient-pan bg-gradient-to-r from-brand-700 via-amber-500 to-brand-700 bg-clip-text text-transparent">
              bringing in guests
            </span>{' '}
            — or sending them elsewhere?
          </h1>
          <p
            className="animate-fade-up delay-var mt-5 max-w-xl text-lg text-ink-600"
            style={d(220)}
          >
            Get a free grade for your restaurant website in about two minutes. We check what Google
            and hungry guests see: your menu, local search visibility, online ordering and more.
          </p>
          <div
            className="animate-fade-up delay-var mt-8 flex flex-wrap items-center gap-3"
            style={d(340)}
          >
            <Link to="/grade" className={buttonClasses('primary', 'lg', 'cta-attention')}>
              Grade My Restaurant <ArrowRight className="cta-arrow size-4" aria-hidden />
            </Link>
            <a href="#how-it-works" className={buttonClasses('ghost', 'lg')}>
              How it works
            </a>
          </div>
        </div>
        <ExampleScoreCard />
      </div>
    </section>
  );
}

export function HowItWorks() {
  const steps = [
    {
      Icon: Search,
      title: 'Find your restaurant',
      body: 'Search by name and city. We pull your public business listing.',
    },
    {
      Icon: Globe,
      title: 'Confirm your website',
      body: 'We detect your website automatically — you can correct it if needed.',
    },
    {
      Icon: BarChart3,
      title: 'Get your grade',
      body: 'We scan your site and score it against 80+ checks built for restaurants.',
    },
  ];
  return (
    <section id="how-it-works" className="scroll-mt-20 border-y border-ink-200 bg-white py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal>
          <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">How it works</h2>
        </Reveal>
        <ol className="mt-12 grid gap-8 md:grid-cols-3">
          {steps.map(({ Icon, title, body }, i) => (
            <Reveal
              as="li"
              key={title}
              delay={i * 150}
              className="group rounded-2xl bg-cream p-6 ring-1 ring-ink-200 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:ring-brand-300"
            >
              <span className="grid size-11 place-items-center rounded-xl bg-brand-700 text-white transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
                <Icon className="size-5" aria-hidden />
              </span>
              <p className="mt-4 text-sm font-semibold text-brand-700">Step {i + 1}</p>
              <h3 className="mt-1 text-lg font-bold">{title}</h3>
              <p className="mt-2 text-ink-600">{body}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function WhatWeCheck() {
  const items = [
    {
      Icon: Search,
      title: 'Google search basics',
      body: 'Page titles, descriptions, headings, sitemaps and whether Google can read your site at all.',
    },
    {
      Icon: MapPin,
      title: 'Local visibility',
      body: 'Your address, phone and hours — and whether they match your business listing.',
    },
    {
      Icon: UtensilsCrossed,
      title: 'Restaurant essentials',
      body: 'Is your menu readable text or a hard-to-find PDF? Can guests order online or book a table?',
    },
    {
      Icon: Zap,
      title: 'Mobile experience',
      body: 'Most guests find you on their phone. We check your site is built for it.',
    },
  ];
  return (
    <section className="py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal className="max-w-2xl">
          <h2 className="font-display text-3xl font-bold sm:text-4xl">
            What we check — in plain English
          </h2>
          <p className="mt-3 text-lg text-ink-600">
            No jargon. Every issue comes with why it matters and exactly how to fix it.
          </p>
        </Reveal>
        <ul className="mt-10 grid gap-6 sm:grid-cols-2">
          {items.map(({ Icon, title, body }, i) => (
            <Reveal
              as="li"
              key={title}
              delay={i * 120}
              variant={i % 2 ? 'right' : 'left'}
              className="group flex gap-4 rounded-2xl bg-white p-6 ring-1 ring-ink-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:ring-brand-300"
            >
              <Icon
                className="size-6 shrink-0 text-brand-700 transition-transform duration-300 group-hover:scale-125 group-hover:-rotate-6"
                aria-hidden
              />
              <div>
                <h3 className="font-bold">{title}</h3>
                <p className="mt-1 text-ink-600">{body}</p>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function ExampleRecommendations() {
  const recs = [
    {
      sev: 'Critical',
      cls: 'bg-red-100 text-red-800',
      title: 'Google can’t read your menu',
      fix: 'Publish your menu as text on a web page instead of a PDF so you can rank for dishes like “best carbonara near me”.',
    },
    {
      sev: 'High',
      cls: 'bg-orange-100 text-orange-800',
      title: 'Your opening hours aren’t on your website',
      fix: 'Add your hours as text on your homepage and location page.',
    },
    {
      sev: 'Quick win',
      cls: 'bg-emerald-100 text-emerald-800',
      title: 'Your homepage title doesn’t include your city',
      fix: 'Change it to “Joe’s Italian Kitchen | Italian Restaurant in Austin, TX”.',
    },
  ];
  return (
    <section className="border-y border-ink-200 bg-white py-20">
      <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <div>
          <h2 className="font-display text-3xl font-bold sm:text-4xl">
            Clear fixes, ranked by impact
          </h2>
          <p className="mt-3 text-lg text-ink-600">
            We put the problems that cost you the most guests first, and highlight quick wins you
            can fix this afternoon.
          </p>
          <ul className="mt-6 space-y-2 text-ink-700">
            {[
              'More visibility in “near me” searches',
              'More online orders and reservations',
              'Fewer guests lost to competitors',
            ].map((b) => (
              <li key={b} className="flex items-center gap-2">
                <CheckCircle2 className="animate-pop size-5 text-emerald-600" aria-hidden /> {b}
              </li>
            ))}
          </ul>
        </div>
        <ul className="space-y-4">
          {recs.map((r, i) => (
            <Reveal
              as="li"
              key={r.title}
              delay={i * 150}
              variant="right"
              className="rounded-2xl bg-cream p-5 ring-1 ring-ink-200 transition-all duration-300 hover:-translate-x-1 hover:shadow-lg"
            >
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.cls}`}>
                {r.sev}
              </span>
              <h3 className="mt-2 font-semibold">{r.title}</h3>
              <p className="mt-1 text-sm text-ink-600">{r.fix}</p>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function CompetitorTeaser() {
  return (
    <section className="py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal
          variant="zoom"
          className="animate-gradient-pan rounded-3xl bg-gradient-to-br from-ink-900 via-brand-900 to-ink-900 p-8 text-white shadow-2xl sm:p-12"
        >
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-300">
            Included in every report
          </p>
          <h2 className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">
            See how you stack up against nearby competitors
          </h2>
          <p className="mt-3 max-w-2xl text-ink-300">
            Your report compares your reviews, rating and website speed with similar restaurants
            nearby — with the source and date shown for every number.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function FinalCta() {
  return (
    <section className="pb-24">
      <Reveal variant="zoom" className="mx-auto max-w-3xl px-4 text-center sm:px-6">
        <h2 className="font-display text-3xl font-bold sm:text-4xl">
          Find out your score in two minutes
        </h2>
        <p className="mt-3 text-lg text-ink-600">Free, private, and no account required.</p>
        <Link to="/grade" className={buttonClasses('primary', 'lg', 'cta-attention mt-8')}>
          Grade My Restaurant <ArrowRight className="cta-arrow size-4" aria-hidden />
        </Link>
      </Reveal>
    </section>
  );
}
